import React, { useCallback, useEffect, useState, useContext } from "react";
import { Layout, Spin, Button, Alert, Progress, Drawer } from "antd";

import { useQuery, /* debug */ useMutation } from "@apollo/react-hooks";
import { useCubeQuery } from "@cubejs-client/react";
import { Icon } from "@ant-design/compatible";
import { GET_DASHBOARD_ITEMS } from "../graphql/queries";

import ChartRenderer from "../components/ChartRenderer";
import Dashboard from "../components/Dashboard";
import DashboardItem from "../components/DashboardItem";
import Filters from "../components/DashboardFilters/Filters";
import ProgressModal from "../components/ProgressModal";
import NotAuthenticated from "../components/auth0/NotAuthenticated";
import LoadingScreen from "../components/LoadingScreen";
import DashboardHeader from "../components/DashboardHeader";

import { useAuth0 } from "@auth0/auth0-react";

import AppContext from "../context/AppContext";

import styles from "./styles";
import chart from "../utils/chart";
import time from '../utils/time'
import dashboardFilter from '../utils/dashboardFilter'
import filter from "../dashboard/filter.json";

import useQueryUpdater from '../hooks/useQueryUpdater'
import useQueryReducer from '../hooks/useQueryReducer'

const deserializeItem = (i) => ({
  ...i,
  layout: JSON.parse(i.layout) || {},
  vizState: JSON.parse(i.vizState),
});

const defaultLayout = (i) => ({
  x: i.layout.x || 0,
  y: i.layout.y || 0,
  w: i.layout.w || 4,
  h: i.layout.h || 8,
  minW: 4,
  minH: 8,
});

const DashboardPage = ({ location }) => {
  const { user, isAuthenticated, isLoading } = useAuth0();

  const { Content } = Layout

  // useQueryUpdater custom hook
  const queryUpdater = useQueryUpdater()
  const updateDashboardItem = async (dashboardItem) => {
    await (await queryUpdater).updateQuery(dashboardItem)
  }

  // useQueryReducer custom hook
  const { state, dispatch, clearAllFilters } = useQueryReducer()


  // useStates 
  const [showFilter, setShowFilter] = useState(false);
  const [update, setUpdate] = useState(false);
  const [dashboardExport, setDashboardExport] = useState(false);
  const [currentTab, setCurrentTab] = useState("0")


  // useContext
  const { exportProgress, setExportProgress } = useContext(AppContext);

  const { loading, error, data, startPolling, stopPolling } = useQuery(
    GET_DASHBOARD_ITEMS,
    {
      fetchPolicy: "cache-and-network",
    }
  );


  // useCallbacks
  const onTabSelected = useCallback((value) => {
    setCurrentTab(value)
  })

  const onFilterChanged = useCallback((key, value) => {
    let actionItem = {}
    actionItem[key] = value
    dispatch(actionItem)
  })

  const onApplyClicked = useCallback(() => {
    console.log("apply clicked,", state)
    setShowFilter(false)
    let updatedFilter = dashboardFilter.mapStateToFilters(state)
    data.dashboardItems.forEach(dashboardItem => {
      let vizState = JSON.parse(dashboardItem.vizState)
      let timeDimensions = vizState.query.timeDimensions

      let updatedTimeDimensions = time.mapDateRangeToTimeDimensions(state.date_range, 'Talkwalker', 'published', timeDimensions)
      vizState.query.filters = updatedFilter
      vizState.query.timeDimensions = updatedTimeDimensions
      console.log("vizState new", vizState)
      dashboardItem.vizState = JSON.stringify(vizState)
      updateDashboardItem(dashboardItem)
    })

  })

  const onResetAllClicked = useCallback(() => {
    console.log("reset clicked")
    clearAllFilters()
    setShowFilter(false)
    data.dashboardItems.forEach(dashboardItem => {
      let vizState = JSON.parse(dashboardItem.vizState)
      vizState.query.filters = []
      let updatedTimeDimensions = time.mapDateRangeToTimeDimensions(state.date_range, 'Talkwalker', 'published', vizState.query.timeDimensions)
      vizState.query.timeDimensions = updatedTimeDimensions
      dashboardItem.vizState = JSON.stringify(vizState)
      updateDashboardItem(dashboardItem)

    })
  })

  const onUploadComplete = useCallback(() => {
    setUpdate(true);
    startPolling(5000);
    setTimeout(() => {
      stopPolling();
      setUpdate(false);
    }, 20000);
  });

  const handleExport = useCallback(async () => {
    setDashboardExport(true);

    // console.log("sleep 500 milliseconds")
    // await new Promise(resolve => setTimeout(resolve, 500))

    let success = await chart.uploadToBucket(exportProgress, setExportProgress);
    console.log(success);
    // setExportProgress({
    //   ...exportProgress,
    //   status: 'active',
    //   message: 'Export complete. The report will be downloaded shortly.'
    // })
    let reportSuccess = await chart.downloadReport(
      success.reportId,
      exportProgress,
      setExportProgress
    );
    console.log(reportSuccess);
  });


  // useEffects

  useEffect(() => {
    chart.setSelectedTab(currentTab)
    dashboardFilter.setDimensionStates()
    time.setDateRange()
    clearAllFilters()
    const currentDashboardItems = chart.getDashboardItemsForTab(currentTab)
    chart.setLocalDashboardItems(currentDashboardItems.dashboardItems)
    currentDashboardItems.dashboardItems
      .forEach((dashItem) => {
        updateDashboardItem(dashItem)
      })

  }, [currentTab])

  useEffect(() => {
    // cache the dashboard
    chart.setDashboard();
    dashboardFilter.setDimensionOptions();
    if (data) {
      // console.log(data)
    }
  }, []);

  if (loading) {
    return <LoadingScreen text="loading" />;
  }

  if (error) {
    return (
      <Alert
        message="Error occured while loading your query"
        description={error.toString()}
        type="error"
      />
    );
  }

  const dashboardItem = (item) => (
    <div key={item.id} data-grid={defaultLayout(item)}>
      <DashboardItem key={item.id} itemId={item.id} title={item.name}>
        <ChartRenderer
          vizState={item.vizState}
          amchartId={item.id}
          amchartTitle={item.name}
        />
      </DashboardItem>
    </div>
  );

  const Empty = () => (
    <div style={styles.empty()}>
      {isAuthenticated && (
        <>
          <h2>There are no charts on this dashboard</h2>
        </>
      )}
    </div>
  );

  // if authentication is in progress
  //if (isLoading)
  //return (
  //  <div>
  //   <div> Authenticating ...</div>
  //  </div>
  //);

  return (
    <>
      {!isLoading && !isAuthenticated && <NotAuthenticated />}
      {isAuthenticated && (!data || data.dashboardItems.length) ? (
        <>
          {update ? (
            <Spin
              tip={`Please wait while we load the new data`}
              size="large"
              spinning={update}
              style={styles.load()}
            />
          ) : (
            <>
              <ProgressModal
                showModal={dashboardExport}
                setShowModal={setDashboardExport}
                progressState={exportProgress}
                setProgressState={setExportProgress}
              />

              <DashboardHeader
                setShowFilter={setShowFilter}
                handleChange={onTabSelected}
              />
              <Drawer
                placement="right"
                closable={false}
                onClose={() => setShowFilter(false)}
                visible={showFilter}
                width={0.7 * window.innerWidth}
              >
                <Filters
                  dimensionOptions={dashboardFilter.getDimensionOptions()}
                  tableName="Talkwalker"
                  timeColumn="published"
                  filterState={state}
                  handleFilterChange={onFilterChanged}
                  handleApply={onApplyClicked}
                  handleResetAll={onResetAllClicked}
                  afterUpload={onUploadComplete}
                  handleExport={handleExport}
                />
              </Drawer>
              <Dashboard dashboardItems={data && data.dashboardItems}>
                {data &&
                  data.dashboardItems.map(deserializeItem).map(dashboardItem)}
              </Dashboard>


            </>
          )}
        </>
      ) : (
        <Empty />
      )}
    </>
  );
};

export default DashboardPage;
